/**
 * Time Entry schemas — staff log hours against compliance items / clients.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, pageInput, sortOrderSchema } from './common';

export const timeEntryOutputSchema = z.object({
  id: idSchema,
  userId: idSchema,
  engagementId: idSchema.nullable(),
  clientId: idSchema.nullable(),              // CRM client (legacy)
  complianceClientId: idSchema.nullable(),
  complianceItemId: idSchema.nullable(),      // NEW: link to specific item (Phase 1+)
  engagement: z.object({
    id: idSchema,
    title: z.string().nullable(),
    client: z.object({ name: z.string(), company: z.string().nullable() }),
  }).nullable().optional(),
  complianceClient: z.object({ id: idSchema, name: z.string(), company: z.string().nullable() }).nullable().optional(),
  complianceItem: z.object({ id: idSchema, serviceType: z.string(), jurisdiction: z.string().nullable() }).nullable().optional(),
  date: iso8601Schema,
  hours: z.number().min(0).multipleOf(0.05),
  description: z.string().max(500).nullable(),
  billable: z.boolean().default(true),
  running: z.boolean().default(false),
  startedAt: iso8601Schema.nullable(),
  pausedAt: iso8601Schema.nullable(),
  pausedSeconds: z.number().int().nonnegative().default(0),
  invoicedAt: iso8601Schema.nullable(),
  invoiceId: idSchema.nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type TimeEntryOutput = z.infer<typeof timeEntryOutputSchema>;

const createTimeEntryBaseSchema = z.object({
  engagementId: idSchema.nullable().optional(),
  complianceClientId: idSchema.nullable().optional(),
  complianceItemId: idSchema.nullable().optional(),
  date: iso8601Schema,
  hours: z.number().min(0).multipleOf(0.05).max(24),
  description: z.string().max(500).nullable().optional(),
  billable: z.boolean().default(true),
  running: z.boolean().default(false),
  startedAt: iso8601Schema.nullable().optional(),
});

export const createTimeEntryInputSchema = createTimeEntryBaseSchema.refine(
  (v) => Boolean(v.engagementId) || Boolean(v.complianceClientId) || Boolean(v.complianceItemId),
  { message: 'engagementId, complianceClientId, or complianceItemId is required' },
);
export type CreateTimeEntryInput = z.infer<typeof createTimeEntryBaseSchema>;

export const updateTimeEntryInputSchema = createTimeEntryBaseSchema.partial();
export type UpdateTimeEntryInput = z.infer<typeof updateTimeEntryInputSchema>;

export const listTimeEntriesInputSchema = pageInput.extend({
  userId: idSchema.optional(),
  engagementId: idSchema.optional(),
  complianceClientId: idSchema.optional(),
  complianceItemId: idSchema.optional(),
  from: iso8601Schema.optional(),
  to: iso8601Schema.optional(),
  billable: z.boolean().optional(),
  invoiced: z.boolean().optional(),            // false = un-invoiced only
  sortBy: z.enum(['date', 'createdAt', 'user', 'hours', 'billable']).default('date'),
  sortOrder: sortOrderSchema,
});
export type ListTimeEntriesInput = z.infer<typeof listTimeEntriesInputSchema>;
export const startTimeEntryInputSchema = z.object({
  engagementId: idSchema.nullable().optional(),
  complianceClientId: idSchema.nullable().optional(),
  complianceItemId: idSchema.nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  billable: z.boolean().default(true),
}).refine(
  (v) => Boolean(v.engagementId) || Boolean(v.complianceClientId) || Boolean(v.complianceItemId),
  { message: 'engagementId, complianceClientId, or complianceItemId is required' },
);
export type StartTimeEntryInput = z.infer<typeof startTimeEntryInputSchema>;

export const stopTimeEntryInputSchema = z.object({});
export type StopTimeEntryInput = z.infer<typeof stopTimeEntryInputSchema>;
